import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import EmailHtml from 'assets/components/html-templates/email/EmailTemplate';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import RichTextInput from 'assets/components/inputs/richTextInput/RichTextInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { set } from 'lodash';
import Role from 'models/core/role/Model.Role';
import ClaimJobEmailTemplateTags from 'module/management/components/claimJobEmailTemplate/ClaimJobEmailTemplateTags';
import { renderToString } from 'react-dom/server';
import { useClaimJobEmailTemplateProvider } from '../ClaimJobEmailTemplate.Hooks';

export default function ClaimJobEmailTemplateEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, saveApi, availableStatuses } = useClaimJobEmailTemplateProvider();
  const { payload: rolesPayload } = useApi({ action: Role.list });

  return (
    <>
      <ModalForm<Api.Core.ClaimJobEmailTemplate.Save.IRequest>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected]}
        title={lang.emailTemplate}
        default={
          selected
            ? {
                ...selected.toJSON(),
                languageIsoCode: selected?.languageIsoCode ?? Language.english,
              }
            : {
                htmlBody: renderToString(EmailHtml({ body: 'Message' })),
                additionalRecipientEmails: [],
                languageIsoCode: Language.english,
              }
        }
        onCancel={pageRouter.updateParams}
        onSubmit={saveApi.execute}
        validation={(data, errors) => {
          if (
            !data.additionalRecipientEmails?.some((it) => it) &&
            !data.recipientsWithRole?.some((it) => it.id) &&
            !data.sendToClaimOwner &&
            !data.sendToClient &&
            !data.sendToContactEmail
          )
            set(errors, 'additionalRecipientEmails', lang.mustNotBeEmpty);
          if (data.additionalRecipientEmails?.length && data.additionalRecipientEmails?.some((it) => !isEmail(it)))
            set(errors, 'additionalRecipientEmails', lang.invalidEmailFormatRequires);
          if (!data.subject) set(errors, 'subject', lang.mustNotBeEmpty);
          if (!data.senderEmail) set(errors, 'senderEmail', lang.mustNotBeEmpty);
          if (data.senderEmail && !isEmailDomain(data.senderEmail, 'goautoshield.com'))
            set(errors, 'senderEmail', lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com'));
          if (!data.name) set(errors, 'name', lang.mustNotBeEmpty);
          if (!data.senderName) set(errors, 'senderName', lang.mustNotBeEmpty);
          if (!data.htmlBody) set(errors, 'htmlBody', lang.mustNotBeEmpty);
          if (!data.claimJobStatus) set(errors, 'claimJobStatus', lang.mustNotBeEmpty);
        }}
        render={({ form }) => (
          <>
            <FormWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={form.data.name}
                error={form.errors.name}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <InputWrapper>
                <TextInput
                  name="senderName"
                  label={lang.senderName}
                  value={form.data.senderName}
                  error={form.errors.senderName}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="senderEmail"
                  label={lang.senderEmail}
                  value={form.data.senderEmail}
                  error={form.errors.senderEmail}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
              </InputWrapper>
              <SelectInput
                name="claimJobStatus"
                label={lang.claimJobStatus}
                data={availableStatuses?.map((status) => ({
                  id: status,
                  title: (lang[status] ?? status)?.replace(/_/g, ' - '),
                  iconClass: 'fas-box',
                }))}
                value={form.data.claimJobStatus}
                error={form.errors.claimJobStatus}
                onChange={form.update}
                icon={<Icon class="if-cloud" />}
              />
              <h2>{lang.sendTo}</h2>
              <CheckWrapper>
                <CheckInput
                  name="sendToClient"
                  label={lang.client}
                  class={inputStyles.check.switch}
                  value={form.data.sendToClient}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data.sendToClient ? lang.disable : lang.enable }}
                />
                <CheckInput
                  name="sendToContactEmail"
                  label={lang.contact}
                  class={inputStyles.check.switch}
                  value={form.data.sendToContactEmail}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data.sendToContactEmail ? lang.disable : lang.enable }}
                />
                <CheckInput
                  name="sendToClaimOwner"
                  label={lang.claimOwner}
                  class={inputStyles.check.switch}
                  value={form.data.sendToClaimOwner}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data.sendToClaimOwner ? lang.disable : lang.enable }}
                />
              </CheckWrapper>
              &nbsp;
              <SelectInput
                name="recipientsWithRole"
                label={lang.recipientsWithRole}
                data={rolesPayload?.data?.map((item) => ({
                  ...item.displayInfo,
                }))}
                value={form.data.recipientsWithRole?.map((it) => it.id)}
                isMultiselect
                onChange={({ recipientsWithRole: id }) =>
                  form.update({ recipientsWithRole: id?.map((id) => ({ id })) })
                }
                icon={<Icon class="if-cloud" />}
              />
              <h2>{lang.additionalRecipientEmails}</h2>
              <DataMap
                data={[...(form.data.additionalRecipientEmails || []), '']}
                render={({ data: email, index }) => (
                  <TextInput
                    key={index}
                    name="email"
                    label={lang.email}
                    value={email}
                    error={form.errors.additionalRecipientEmails}
                    onChange={({ email }) => {
                      let additionalRecipientEmails = form.data.additionalRecipientEmails || [];
                      additionalRecipientEmails.splice(index, 1, email);
                      additionalRecipientEmails = additionalRecipientEmails.filter((it) => it);
                      form.update({ additionalRecipientEmails });
                    }}
                    icon={<Icon class="if-quill-pen" />}
                  />
                )}
              />
              <h2>{lang.email}</h2>
              <TextInput
                name="subject"
                label={lang.subject}
                value={form.data.subject}
                error={form.errors.subject}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <RichTextInput
                name="htmlBody"
                label={lang.htmlBody}
                value={form.data.htmlBody}
                error={form.errors.htmlBody}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
                customActions={<ClaimJobEmailTemplateTags />}
              />
            </FormWrapper>
          </>
        )}
      />
    </>
  );
}
